import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { getPromotions, PromotionAudience, PromotionCategory } from "@/lib/promotions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export type PromotionSpotlightProps = {
  title?: string;
  description?: string;
  audience?: PromotionAudience | PromotionAudience[];
  categories?: PromotionCategory[];
  excludeIds?: string[];
  limit?: number;
  className?: string;
};

export function PromotionSpotlight({
  title = "Recommended upgrades",
  description = "Boost trust and visibility with these suggested add-ons.",
  audience,
  categories,
  excludeIds,
  limit = 2,
  className,
}: PromotionSpotlightProps) {
  const promotions = useMemo(
    () => getPromotions({ audience, categories, excludeIds, limit }),
    [audience, categories, excludeIds, limit]
  );

  if (promotions.length === 0) {
    return null;
  }

  return (
    <section className={cn("space-y-4", className)}>
      <header className="space-y-1">
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        {description ? (
          <p className="text-sm text-muted-foreground">{description}</p>
        ) : null}
      </header>
      <div className="grid gap-4 md:grid-cols-2">
        {promotions.map((promotion) => (
          <Card key={promotion.id} className="border-muted bg-muted/40">
            <CardHeader className="flex-row items-center gap-3">
              <div className="rounded-full bg-primary/10 p-2 text-primary">
                <promotion.icon className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">
                  {promotion.title}
                </CardTitle>
                <CardDescription className="text-sm">
                  {promotion.description}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <Button variant="secondary" asChild className="w-full">
                <a href={promotion.href}>{promotion.ctaLabel}</a>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}
